import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { BpsAdjustment, LineItem } from '../types/commission';
interface OverrideBpsModalProps {
  item: LineItem;
  existingReason?: string;
  onCancel: () => void;
  onSave: (value: BpsAdjustment) => void;
}
const SAVE_DELAY = 600;
export function OverrideBpsModal({
  item,
  existingReason = '',
  onCancel,
  onSave
}: OverrideBpsModalProps) {
  const [bps, setBps] = useState(item.bps !== null ? String(item.bps) : '');
  const [reason, setReason] = useState(existingReason);
  const [saving, setSaving] = useState(false);
  const timer = useRef<number>();
  const value = Number(bps);
  const isValid = bps.trim() !== '' && !Number.isNaN(value) && value >= 0.1;
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !saving) onCancel();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, saving]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  function handleSave() {
    if (!isValid) return;
    setSaving(true);
    timer.current = window.setTimeout(() => onSave({
      bps: value,
      reason: reason.trim()
    }), SAVE_DELAY);
  }
  return <>
      <div role="presentation" className="fixed inset-0 z-40 bg-slate-900/50" onClick={() => saving ? undefined : onCancel()} data-id="element-370" data-mp="ustnruzj0yde" />
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-371" data-mp="g2m63hhcfjdr">
        <motion.section role="dialog" aria-modal="true" aria-labelledby="override-modal-title" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.16,
        ease: 'easeOut'
      }} className="pointer-events-auto w-full max-w-md rounded-xl bg-white shadow-xl" data-id="element-372" data-mp="kx06jyav4gpa">
          <header className="border-b border-slate-200 px-6 py-4" data-id="element-373" data-mp="23cterscj7ej">
            <h2 id="override-modal-title" className="text-base font-bold text-slate-900" data-id="element-374" data-mp="23ud4ujb3ndcj">
              Override BPS
            </h2>
            <p className="mt-0.5 text-sm text-slate-500" data-id="element-375" data-mp="1z1k4e7mnyr69">
              {item.loan.borrower} · {item.loan.id}
            </p>
          </header>
          <div className="space-y-4 px-6 py-5" data-id="element-376" data-mp="1q2f2crbw8fpc">
            <label className="block" data-id="element-377" data-mp="1q3qbz7s0shod">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-378" data-mp="1q0lzzo8bchn5">BPS</span>
              <input type="number" step="0.1" min="0.1" autoFocus value={bps} onChange={event => setBps(event.target.value)} className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm tabular-nums text-slate-900" data-id="element-379" data-mp="2ir49p83jlh9j" />
            </label>
            <label className="block" data-id="element-380" data-mp="12armd4g66p1u">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-381" data-mp="xd86y17qk68b">Reason (optional)</span>
              <textarea rows={3} value={reason} onChange={event => setReason(event.target.value)} className="mt-1.5 w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900" data-id="element-382" data-mp="navtflcibg2o" />
            </label>
          </div>
          <footer className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4" data-id="element-383" data-mp="120a3kvnwq1j4">
            <button type="button" onClick={onCancel} disabled={saving} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60" data-id="element-384" data-mp="15ziyw4yli7gs">
              Cancel
            </button>
            <button type="button" onClick={handleSave} disabled={saving || !isValid} className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:bg-brand-300" data-id="element-385" data-mp="3hqek90h9lj05">
              {saving ? 'Saving…' : 'Save override'}
            </button>
          </footer>
        </motion.section>
      </div>
    </>;
}